import AvatarsStack from "common/components/AvatarsStack/AvatarsStack";
import TooltipWrapper from "common/components/TooltipWrapper/TooltipWrapper";
import WeatherInfo from "common/components/WeatherInfo/WeatherInfo";
import useList from "common/hooks/useList";
import useOrganization from "common/hooks/useOrganization";
import Icon, { IconNames } from "components/Icon/Icon";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { User } from "types";
import { Tag } from "types/Tag";
import { Thread } from "types/Thread";
import { Weather } from "types/Weather";
import Modal from "common/components/Modal/Modal";
import { useTranslation } from "react-i18next";

interface IHeaderInfos {
  text?: string;
  textPlace?: string;
  isTextClickable?: boolean;
  textRedirectionLink?: string;
  textPlaceRedirectionLink?: string;
  assignedUsers?: User[];
  tags?: Tag[];
  positionIcon?: IconNames;
  selectedThread?: Thread;
}

function HeaderInfos({
  text,
  textPlace,
  assignedUsers,
  tags,
  positionIcon = "WorkSiteIcon",
  isTextClickable = false,
  textRedirectionLink,
  textPlaceRedirectionLink,
  selectedThread,
}: IHeaderInfos) {
  const { t } = useTranslation();
  const [showWeatherModal, setShowWeatherModal] = useState(false);
  
  const {
    items: weatherItems,
    isFetching: weatherLoading,
    setFilters: setWeatherFilters,
  } = useList<Weather>("weathers", {
    defaultSort: "date:ASC",
    ready: Boolean(selectedThread?.createdAt),
    cachePrefix: `Weather_${selectedThread?.id || "all"}`,
  });

  useEffect(() => {
    if (!selectedThread?.createdAt) {
      return;
    }
    const baseDate = dayjs(selectedThread.createdAt).startOf("hour");

    setWeatherFilters({
      date$gte: baseDate.subtract(1, "hour").toISOString(),
      date$lte: baseDate.add(1, "hour").add(20, "minute").toISOString(),
    });
  }, [selectedThread, setWeatherFilters]);

  const { organization } = useOrganization();
  return (
    <div className="gap-3 items-center xl:items-start grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2">
      {text && (
        <div className="flex gap-1 items-center font-semibold text-neutral-600">
          <Icon name={positionIcon} className="w-4 h-4" />
          {isTextClickable ? (
            <a target="_blank" rel="noreferrer" href={textRedirectionLink}>
              {text}
            </a>
          ) : (
            text
          )}
        </div>
      )}

      {textPlace && textPlaceRedirectionLink && (
        <div className="flex gap-1 items-center font-semibold text-emerald-700 hover:text-emerald-600">
          <Icon name="MapIcon" className="w-4 h-4" />

          <a target="_blank" rel="noreferrer" href={textPlaceRedirectionLink}>
            {textPlace}
          </a>
        </div>
      )}

      {!!organization?.weatherEnabled && (
        <div className="flex gap-1 items-center">
          <button
            type="button"
            onClick={() => setShowWeatherModal(true)}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
          >
            <Icon name="EyeIcon" className="w-4 h-4" />
            {t("common.seeWeather")}
          </button>
        </div>
      )}

      {tags && tags?.length > 0 && (
        <div
          className={`flex items-center gap-1 ${
            text && "border-l border-slate-200 pl-3"
          }`}
        >
          <TooltipWrapper
            place="bottom"
            className="mr-1 my-1"
            content={
              tags?.length > 3 ? (
                <div className="flex flex-col gap-2">
                  {tags?.map((tag: Tag) => (
                    <span>{tag.label}</span>
                  ))}
                </div>
              ) : null
            }
          >
            <div className="flex flex-row items-center">
              <Icon name="TagIcon" className="w-4 h-4 mr-2" />
              {tags?.map((tag: Tag, index: number) =>
                index < 3 ? (
                  <span>{(index > 0 ? ", " : "") + tag.label}</span>
                ) : null
              )}

              {tags?.length > 3 && <div>...</div>}
            </div>
          </TooltipWrapper>
        </div>
      )}

      {assignedUsers && assignedUsers?.length > 0 && (
        <div
          className={`flex items-center gap-1 ${
            text && "border-l border-slate-200"
          }`}
        >
          <AvatarsStack users={assignedUsers} size="8" />
        </div>
      )}

      {/* Weather Modal */}
      {showWeatherModal && (
        <Modal
          visible={showWeatherModal}
          onCloseModal={() => setShowWeatherModal(false)}
          title={t("common.weather")}
          showCloseModalFooter
        >
          <div className="py-4">
            <WeatherInfo items={weatherItems} loading={weatherLoading} />
          </div>
        </Modal>
      )}
    </div>
  );
}

export default HeaderInfos;
